import { combineSubscribers } from "@knyt/artisan";
import {} from "@knyt/weaver";
import { ComponentController } from "./ComponentController.js";
class ComponentControllerRegistry {
    #controllers = new WeakMap();
    #getController(element, options) {
        const existingController = this.#controllers.get(element);
        if (existingController) {
            return existingController;
        }
        const newController = new ComponentController(options);
        this.#controllers.set(element, newController);
        return newController;
    }
    #createRootElementReferenceFactory(options) {
        return ({ props, children }) => {
            return (el) => {
                // We don't need to handle previous roots, because the controller
                // will clean itself up when the root element is removed.
                //
                // We don't want to assume if the renderer discards the previous root
                // element or not, so we'll leave the controller to clean itself up.
                if (!el)
                    return;
                const controller = this.#getController(el, options);
                controller.root$.set(el);
                controller.latestChildren$.set(children);
                controller.setProps(props);
            };
        };
    }
    /**
     * Extracts the element props from the component props.
     */
    #splitProps(properties, props) {
        const reactivePropValues = {};
        const elementPropValues = {};
        for (const key in props) {
            if (key in properties) {
                reactivePropValues[key] = props[key];
            }
            else {
                elementPropValues[key] = props[key];
            }
        }
        return {
            reactivePropValues,
            elementPropValues,
        };
    }
    /**
     * @internal scope: package
     */
    createViewRenderer(options) {
        const controllerRootReferenceFactory = this.#createRootElementReferenceFactory(options);
        const renderView = (allProps, { children, ref: externalRoot$, key }) => {
            const { reactivePropValues, elementPropValues } = this.#splitProps(options.properties, allProps);
            const controllerRoot$ = controllerRootReferenceFactory({
                props: reactivePropValues,
                children,
            });
            const rootBuilder = options.root;
            const root$ = combineSubscribers(externalRoot$, controllerRoot$);
            return (rootBuilder
                .$renderMode("opaque")
                .$ref(root$)
                .$key(key)
                .$props(
            // Type cast to ignore an unhelpful type error.
            // It's not worth the effort to fix this,
            // because the type for `elementPropValues` is correct.
            elementPropValues)
                // Remove all children from the root element builder,
                // because the children are rendered separately.
                // This is an opaque element, so it shouldn't have
                // any children.
                .$children());
        };
        return renderView;
    }
}
/**
 * @internal scope: package
 *
 * This is a singleton; however, each controller is associated with
 * a unique element, and the registry relies on a WeakMap to manage
 * the controllers. As a result, the registry does not prevent
 * controllers from being garbage collected when their associated
 * elements are removed from the DOM.
 */
export const globalComponentRegistry = new ComponentControllerRegistry();
